import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import type { TheaterSession, TheaterState } from './TheaterSession';

/** OrientationIndicator: an 85 px rose at the bottom-right of the 3D view. Letters snap the
 * view (A P L R S I), dragging the ring rolls the camera. */
export function Compass({ session, state }: { session: TheaterSession; state: TheaterState }) {
  const [tick, setTick] = useState(0);
  const dragRef = useRef<{ startAngle: number } | null>(null);
  useEffect(() => {
    const prev = session.viewer.onRendered;
    session.viewer.onRendered = (id) => {
      prev?.(id);
      if (id === 'main') setTick((t) => t + 1);
    };
    return () => {
      session.viewer.onRendered = prev;
    };
  }, [session]);
  void tick;

  const cam = session.viewer.controls.camera;
  const camInv = cam.quaternion.clone().invert();
  const axis = (local: THREE.Vector3) => local.clone().transformDirection(session.viewer.root.matrixWorld).normalize().applyQuaternion(camInv);
  const axes = [
    { dir: axis(new THREE.Vector3(1, 0, 0)), pos: state.axisLabels.xPositive, neg: state.axisLabels.xNegative, color: 'rgb(237,69,69)' },
    { dir: axis(new THREE.Vector3(0, 1, 0)), pos: state.axisLabels.yPositive, neg: state.axisLabels.yNegative, color: 'rgb(92,209,92)' },
    { dir: axis(new THREE.Vector3(0, 0, 1)), pos: state.axisLabels.zPositive, neg: state.axisLabels.zNegative, color: 'rgb(84,140,242)' },
  ];
  const size = 85;
  const c = size / 2;
  const alphaFor = (z: number) => 1 - 0.65 * ((z + 1) / 2);

  const angleOf = (e: React.PointerEvent) => {
    const r = (e.currentTarget as SVGElement).getBoundingClientRect();
    return (Math.atan2(e.clientY - (r.top + c), e.clientX - (r.left + c)) * 180) / Math.PI;
  };

  return (
    <svg
      className="compass"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      onPointerDown={(e) => {
        const r = (e.currentTarget as SVGElement).getBoundingClientRect();
        const d = Math.hypot(e.clientX - (r.left + c), e.clientY - (r.top + c));
        if (d >= 29 && d <= 41) {
          dragRef.current = { startAngle: angleOf(e) };
          (e.currentTarget as SVGElement).setPointerCapture(e.pointerId);
          session.viewer.controls.enabled = false;
        }
      }}
      onPointerMove={(e) => {
        if (!dragRef.current) return;
        const a = angleOf(e);
        let delta = a - dragRef.current.startAngle;
        if (delta > 180) delta -= 360;
        if (delta < -180) delta += 360;
        dragRef.current.startAngle = a;
        session.viewer.controls.roll(delta);
      }}
      onPointerUp={(e) => {
        if (dragRef.current) {
          dragRef.current = null;
          (e.currentTarget as SVGElement).releasePointerCapture(e.pointerId);
          session.viewer.controls.enabled = true;
        }
      }}
    >
      <circle cx={c} cy={c} r={35} fill="none" stroke="rgba(217,224,235,0.28)" strokeWidth={1.5} className="compass-ring" />
      {axes.map((a) => {
        const x = a.dir.x * 23;
        const y = -a.dir.y * 23;
        return (
          <g key={a.pos}>
            <line x1={c - x} y1={c - y} x2={c + x} y2={c + y} stroke={a.color} strokeWidth={2} opacity={0.9} />
            <text x={c + (x * 29) / 23} y={c + (y * 29) / 23} fill={a.color} opacity={alphaFor(-a.dir.z)} className="compass-label" onClick={() => session.snapToLabel(a.pos)}>
              {a.pos}
            </text>
            <text x={c - (x * 29) / 23} y={c - (y * 29) / 23} fill={a.color} opacity={alphaFor(a.dir.z)} className="compass-label" onClick={() => session.snapToLabel(a.neg)}>
              {a.neg}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
